import { HttpClient } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, Injectable, NgModule, isDevMode } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, Routes } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AuthClientConfig } from '@auth0/auth0-angular';
import {
  CheckInKioskFeatureCheckInEffects,
  CheckInKioskFeatureCheckInModule,
  CheckInRoutedComponent,
} from '@frontend/check-in-kiosk/feature/check-in/src';
import {
  CheckInKioskFeatureCheckOutEffects,
  CheckInKioskFeatureCheckOutModule,
  CheckOutRoutedComponent,
} from '@frontend/check-in-kiosk/feature/check-out/src';
import { CheckInKioskUiModule } from '@frontend/check-in-kiosk/ui';
import { WelcomeComponent } from '@frontend/check-in-kiosk/ui/src/lib/welcome/welcome.component';
import { CorrelationIdModule } from '@frontend/common/correlation-id';
import { SharedIdleModule } from '@frontend/common/idle';
import { InterceptorModule } from '@frontend/common/interceptor';
import { LoadingModule } from '@frontend/common/loading';
import {
  AUTH_FLOW_DEVICE_ROUTES,
  AUTH_FLOW_LOGOUT_ROUTES,
  AUTH_FLOW_SDK_ROUTES,
  PhAuthGuard,
  PhAuthModule,
  PhAuthServiceConfig,
  PH_AUTH_SERVICE_CONFIG,
  provideBootstrapEffects,
  ScopesGuard,
} from '@frontend/common/ph-auth';
import {
  PhConfigLoaderModel,
  PhConfigLoaderModule,
  PhConfigLoaderService,
} from '@frontend/common/ph-config-loader';
import {
  CustomSerializer,
  RouterEffects,
  routerReducerInitialState,
} from '@frontend/common/ph-router-store';
import { UpdaterConfig, UpdaterModule } from '@frontend/common/updater';
import { setupSentry } from '@frontend/common/util';
import {
  DepartmentApiEffects,
  DepartmentWsEffects,
  SharedDepartmentDataAccessModule,
} from '@frontend/shared/department/data-access/src';
import {
  KioskApiEffects,
  KioskWsEffects,
  SharedKioskDataAccessModule,
} from '@frontend/shared/kiosk/data-access/src';
import {
  SharedSiteDataAccessModule,
  SiteApiEffects,
  SiteWsEffects,
} from '@frontend/shared/site/data-access/src';
import {
  SharedStaffDataAccessModule,
  StaffApiEffects,
  StaffWsEffects,
} from '@frontend/shared/staff/data-access';
import {
  SharedWorkflowDataAccessModule,
  WorkflowApiEffects,
  WorkflowWsEffects,
} from '@frontend/shared/workflow/data-access/src';
import { EffectsModule } from '@ngrx/effects';
import {
  MinimalRouterStateSerializer,
  RouterStateSerializer,
  StoreRouterConnectingModule,
} from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import * as Sentry from '@sentry/angular';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { AppEffects } from './app.effects';
import { AppResolver } from './app.resolver';
import { LoggedInComponent } from './logged-in.component';
import { reducers } from './reducers';
import { initialState } from './reducers/app.reducer';

const routes: Routes = [
  ...AUTH_FLOW_LOGOUT_ROUTES,
  ...AUTH_FLOW_SDK_ROUTES,
  ...AUTH_FLOW_DEVICE_ROUTES,
  { path: '', redirectTo: 'welcome', pathMatch: 'full' },
  {
    path: '',
    data: {
      requiredScopes: ['access:kiosk'],
    },
    canActivate: [PhAuthGuard, ScopesGuard],
    resolve: { appResolver: AppResolver },
    component: LoggedInComponent,
    children: [
      {
        path: '',
        children: [
          { path: 'welcome', component: WelcomeComponent },
          { path: 'check-in', component: CheckInRoutedComponent },
          { path: 'check-out', component: CheckOutRoutedComponent },
        ],
      },
    ],
  },
  { path: '**', redirectTo: 'welcome', pathMatch: 'full' },
];

@Injectable()
export class PhConfigLoaderFactory {
  constructor(private http: HttpClient) {}
  cb = (config: PhConfigLoaderModel): Observable<PhConfigLoaderModel> =>
    this.http.get<{ id: number; auth_org_id: string; auth_device_client_id: string; locale: string }>(
      config.APP_API_URL + 'customers/id'
    ).pipe(
      map(
        (customer) => customer
      ),
      catchError(() =>
        of({
          id: 'disabled',
          auth_org_id: null,
          auth_device_client_id: null,
          locale: null,
        })
      ),
      map((customer) => ({
        ...config,
        CUSTOMER_ID: customer.id,
        AUTH_ORG_ID: customer.auth_org_id,
        AUTH_DEVICE_CLIENT_ID: customer.auth_device_client_id,
        LOCALE: customer.locale,
      })),
      tap(({ SENTRY_DSN }: any) => {
        if (!isDevMode()) {
          setupSentry(
            SENTRY_DSN,
            window.location.href,
            environment.version,
            'check-in-kiosk'
          );
        }
      })
    );
}

@NgModule({
  declarations: [AppComponent, LoggedInComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    StoreModule.forRoot(reducers, {
      metaReducers: [],
      initialState: { routerReducer: routerReducerInitialState, app: initialState },
      runtimeChecks: { strictStateImmutability: true, strictActionImmutability: true },
    }),
    EffectsModule.forRoot([RouterEffects]),
    RouterModule.forRoot(routes),
    StoreRouterConnectingModule.forRoot({ serializer: MinimalRouterStateSerializer }),
    PhConfigLoaderModule.forRoot(PhConfigLoaderFactory),
    !environment.production ? StoreDevtoolsModule.instrument({ maxAge: 100 , connectInZone: true}) : [],
    PhAuthModule,
    CorrelationIdModule, // should not be required by InterceptorModule
    InterceptorModule,
    CheckInKioskFeatureCheckInModule,
    CheckInKioskFeatureCheckOutModule,
    CheckInKioskUiModule,
    SharedKioskDataAccessModule,
    SharedWorkflowDataAccessModule,
    SharedDepartmentDataAccessModule,
    SharedSiteDataAccessModule,
    SharedStaffDataAccessModule,
    SharedIdleModule,
    MatDialogModule,
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
    UpdaterModule.forRoot(
      [PhConfigLoaderService],
      (appConfig: PhConfigLoaderService): UpdaterConfig => ({
        checkInterval: appConfig.getConfig('CHECK_RELEASE_AFTER_SEC'),
      })
    ),
    LoadingModule,
  ],
  providers: [
    AppResolver,
    provideBootstrapEffects([
      AppEffects,
      CheckInKioskFeatureCheckInEffects,
      CheckInKioskFeatureCheckOutEffects,
      KioskApiEffects,
      KioskWsEffects,
      WorkflowApiEffects,
      WorkflowWsEffects,
      DepartmentApiEffects,
      DepartmentWsEffects,
      SiteApiEffects,
      SiteWsEffects,
      StaffApiEffects,
      StaffWsEffects,
    ]),
    {
      provide: APP_INITIALIZER,
      deps: [PhConfigLoaderService, AuthClientConfig],
      useFactory: (ac: PhConfigLoaderService) => () => ac.load(),
      multi: true,
    },
    { provide: RouterStateSerializer, useClass: CustomSerializer },
    {
      provide: ErrorHandler,
      useFactory: () => {
        if (environment.production) {
          return Sentry.createErrorHandler();
        }
        return new ErrorHandler();
      },
    },
    {
      provide: PH_AUTH_SERVICE_CONFIG,
      useFactory: phAuthServiceFactory,
      deps: [PhConfigLoaderService],
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}

function phAuthServiceFactory(configSrv: PhConfigLoaderService): PhAuthServiceConfig {
  return combineLatest({
    APP_API_URL: configSrv.getConfig$('APP_API_URL'),
    AUTH_DOMAIN: configSrv.getConfig$('AUTH_DOMAIN'),
    AUTH_AUDIENCE: configSrv.getConfig$('AUTH_AUDIENCE'),
    AUTH_DEVICE_CLIENT_ID: configSrv.getConfig$('AUTH_DEVICE_CLIENT_ID'),
    REFRESH_TOKEN_IN_ADVANCE_SEC: configSrv.getConfig$('REFRESH_TOKEN_IN_ADVANCE_SEC'),
    AUTH_LOGOUT_CALLBACK_URL: configSrv.getConfig$('AUTH_LOGOUT_CALLBACK_URL'),
    AUTH_ORG_ID: configSrv.getConfig$('AUTH_ORG_ID'),
    LOCALE: configSrv.getConfig$('LOCALE'),
  }).pipe(
    map(({
      APP_API_URL,
      AUTH_DOMAIN,
      AUTH_AUDIENCE,
      AUTH_DEVICE_CLIENT_ID,
      REFRESH_TOKEN_IN_ADVANCE_SEC,
      AUTH_LOGOUT_CALLBACK_URL,
      AUTH_ORG_ID,
      LOCALE,
    }) => ({
      APP_API_URL,
      AUTH_DOMAIN,
      AUTH_AUDIENCE,
      AUTH_DEVICE_CLIENT_ID,
      REFRESH_TOKEN_IN_ADVANCE_SEC,
      AUTH_LOGOUT_CALLBACK_URL,
      AUTH_ORG_ID,
      LOCALE,
    }))
  );
}
